import React, { useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import styles from './Billing.scss';
import { ChargeManager } from 'domains/billingAndPayments/components/ChargeManager';
import { UserType } from 'lane-shared/types/User';
import { ChannelServiceRequestFeaturesType } from 'lane-shared/types/ChannelType';
import {
  ExternalEntityType,
  ExternalPayerType,
  Charge,
} from 'lane-shared/domains/billingPayments/types';
import { convertToUUID } from 'uuid-encoding';
import { ServiceRequestStatusEnum } from 'domains/workOrder/serviceRequest/components/types/serviceRequestStatus';
import { VerifyAndSubmitBilling } from 'domains/workOrder/serviceRequest/components/VerifyAndSubmitBilling/VerifyAndSubmitBilling';
import { useVerifyAndSubmitBilling } from 'domains/workOrder/serviceRequest/verifyAndSubmit/useVerifyAndSubmitBilling';

type Props = {
  channel?: { _id: string; slug?: string };
  user: UserType;
  serviceRequestFeatures: ChannelServiceRequestFeaturesType;
  serviceRequestData: any;
  onSubmitCharge: () => void;
  testId?: string;
};

export function BillingTab({
  serviceRequestFeatures,
  serviceRequestData,
  channel,
  user,
  onSubmitCharge,
  testId,
}: Props) {
  const { hash } = useLocation();
  const { isVerifyFlowEnabled, canVerifyAndSubmit } = useVerifyAndSubmitBilling(
    channel?._id
  );

  useEffect(() => {
    if (!hash) return;
    const chargeId = hash.slice(1);
    // Charges load asynchronously — poll briefly until the element appears.
    let attempts = 0;
    let timeoutId: ReturnType<typeof setTimeout>;
    const tryScroll = () => {
      const el = document.getElementById(chargeId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (++attempts < 10) {
        timeoutId = setTimeout(tryScroll, 300);
      }
    };
    tryScroll();
    return () => clearTimeout(timeoutId);
  }, [hash]);

  const handleSubmitCharge = (charge: Charge) => {
    if (charge.amount > 0) {
      onSubmitCharge();
    }
  };

  const chargeDescription = `${serviceRequestData.category} - ${serviceRequestData.issue}`;

  const metadata = {
    entity_display_id: convertToUUID(serviceRequestData.serviceRequestId),
    entity_display_type: serviceRequestData.category,
    suiteIds: serviceRequestData.suiteIds || [],
  };

  return (
    <div className={styles.BillingTab} data-test={testId}>
      {serviceRequestFeatures.updateServiceRequest &&
        isVerifyFlowEnabled &&
        channel && (
          <VerifyAndSubmitBilling
            channel={channel}
            user={user}
            canVerifyAndSubmit={canVerifyAndSubmit}
            isServiceRequestComplete={
              serviceRequestData.status === ServiceRequestStatusEnum.Complete
            }
            externalEntityType={
              ExternalEntityType.EXTERNAL_ENTITY_TYPE_WORKORDER
            }
            externalPayerId={serviceRequestData.company?.id}
            externalPayerType={
              ExternalPayerType.EXTERNAL_PAYER_TYPE_ACTIVATE_COMPANY
            }
            chargeName={serviceRequestData.userFriendlyID}
            externalEntityId={convertToUUID(
              serviceRequestData.serviceRequestId
            )}
            onSubmitCharge={handleSubmitCharge}
            chargeDescription={chargeDescription}
            metadata={metadata}
          />
        )}
      {serviceRequestFeatures.updateServiceRequest &&
        !(isVerifyFlowEnabled && channel) && (
          <ChargeManager
            channel={channel}
            user={user}
            externalEntityType={
              ExternalEntityType.EXTERNAL_ENTITY_TYPE_WORKORDER
            }
            externalPayerId={serviceRequestData.company?.id}
            externalPayerType={
              ExternalPayerType.EXTERNAL_PAYER_TYPE_ACTIVATE_COMPANY
            }
            chargeName={serviceRequestData.userFriendlyID}
            externalEntityId={convertToUUID(
              serviceRequestData.serviceRequestId
            )}
            onSubmitCharge={handleSubmitCharge}
            chargeDescription={chargeDescription}
            metadata={metadata}
          />
        )}
    </div>
  );
}
